import React from "react";
import { StatusBadge } from "../../../common/comms/StatusBadge";
import { Chip } from "../../../common/comms/Chip";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";
import { relativeTime } from "../../../utils/oversightFormat";

/** Maps the raw `ai_mode` payload value to its short display label. */
const AI_MODE_LABELS = {
  ENABLED: "AI on",
  DISABLED: "AI off",
  URGENCY_ONLY: "AI urgency",
};

/**
 * One responsive room row for the Rooms tab — reads as a compact row on
 * desktop and a stacked card on mobile. The whole card is clickable
 * (and keyboard-activatable) to open the room in the oversight shadow view.
 *
 * @param {Object} props
 * @param {Object} props.room - Room payload from `GET /oversight/rooms`
 *   (`room_id, display_id, client_id, client_name, status, ai_mode,
 *   last_activity_at, message_count, coaches: [{ user_id, resolved_user_id, name }]`).
 * @param {(roomId: string) => void} props.onOpen - Called with `room.room_id` on activation.
 */
export function RoomCard({ room, onOpen }) {
  const { getClientName, getCoachName } = useCommsIdentity();

  const clientName =
    room?.client_name ||
    getClientName(room?.client_id) ||
    (room?.display_id != null ? `#${room.display_id}` : "Unknown client");

  const status = room?.status;
  const statusTone = status === "active" ? "success" : "neutral";

  const aiMode = room?.ai_mode;
  const aiLabel = AI_MODE_LABELS[aiMode] || AI_MODE_LABELS.DISABLED;
  const aiTone = aiMode === "ENABLED" ? "ai" : "neutral";

  const coaches = Array.isArray(room?.coaches) ? room.coaches : [];

  const handleOpen = () => {
    if (room?.room_id != null) onOpen(room.room_id);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleOpen();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
      className="rounded-xl border border-gray-200 bg-white p-4 hover:bg-orange-50/40 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-orange-primary/30"
    >
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <span className="font-semibold text-sm text-gray-900 truncate max-w-[240px]">
          {clientName}
        </span>
        <StatusBadge tone={statusTone}>{status || "—"}</StatusBadge>
        <Chip tone={aiTone}>{aiLabel}</Chip>
      </div>

      <div className="text-xs text-gray-400 mt-1">
        {relativeTime(room?.last_activity_at)} · {room?.message_count ?? 0} msgs
      </div>

      {coaches.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {coaches.map((coach, index) => {
            const id = coach?.resolved_user_id ?? coach?.user_id;
            const name = coach?.name || getCoachName(id) || "Unknown coach";
            return (
              <Chip key={id ?? index} tone="neutral" className="truncate max-w-[160px]">
                {name}
              </Chip>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default RoomCard;
